import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Label } from '../components/ui/label';
import { useAuth } from '../context/AuthContext';
import { usePlayer } from '../context/PlayerContext';
import { toast } from 'sonner';
import { API } from '../config/api';
import { safeJson } from '../utils/safeJson';

import { 
  Play, 
  Trash2, 
  Edit2, 
  Save, 
  X, 
  Music, 
  Clock, 
  User,
  Lock,
  Globe,
  GripVertical,
  Plus,
  ArrowLeft,
  Loader2,
  Shuffle,
  Share2
} from 'lucide-react';


export default function PlaylistPage() {
  const { playlistId } = useParams();
  const navigate = useNavigate();
  const { getAuthHeaders, isAuthenticated, user } = useAuth();
  const { playTrack, playPlaylist, shuffleQueue, clearQueue } = usePlayer();
  
  const [playlist, setPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editData, setEditData] = useState({ name: '', description: '', is_public: true });
  const [removingTrack, setRemovingTrack] = useState(null);

  useEffect(() => {
    fetchPlaylist();
  }, [playlistId, isAuthenticated]);

  const fetchPlaylist = async () => {
    try {
      const headers = isAuthenticated ? getAuthHeaders() : {};
      const response = await fetch(`${API}/playlists/${playlistId}`, { headers });
      
      if (response.ok) {
        const data = await response.json();
        setPlaylist(data);
        setEditData({
          name: data.name,
          description: data.description || '',
          is_public: data.is_public
        });
      } else if (response.status === 404) {
        toast.error('Playlist not found');
        navigate('/playlists');
      } else if (response.status === 403) {
        toast.error('This playlist is private');
        navigate('/playlists');
      }
    } catch (error) {
      console.error('Error fetching playlist:', error);
      toast.error('Failed to load playlist');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveChanges = async () => {
    setSaving(true);
    try {
      const response = await fetch(`${API}/playlists/${playlistId}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(editData)
      });

      if (response.ok) {
        const updated = await response.json();
        setPlaylist(prev => ({ ...prev, ...updated }));
        setEditMode(false);
        toast.success('Playlist updated');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to update playlist');
      }
    } catch (error) {
      toast.error('Failed to update playlist');
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveTrack = async (trackId) => {
    setRemovingTrack(trackId);
    try {
      const response = await fetch(`${API}/playlists/${playlistId}/tracks/${trackId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        setPlaylist(prev => ({
          ...prev,
          tracks: prev.tracks.filter(t => t.track_id !== trackId),
          track_count: prev.track_count - 1
        }));
        toast.success('Track removed from playlist');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to remove track');
      }
    } catch (error) {
      toast.error('Failed to remove track');
    } finally {
      setRemovingTrack(null);
    }
  };

  const handleDeletePlaylist = async () => {
    if (!window.confirm('Are you sure you want to delete this playlist? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API}/playlists/${playlistId}`, {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      if (response.ok) {
        toast.success('Playlist deleted');
        navigate('/playlists');
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Failed to delete playlist');
      }
    } catch (error) {
      toast.error('Failed to delete playlist');
    }
  };

  const playAllTracks = () => {
    if (playlist?.tracks?.length > 0) {
      playPlaylist(playlist.tracks, 0);
    }
  };

  const shufflePlay = () => {
    if (playlist?.tracks?.length > 0) {
      const shuffled = [...playlist.tracks];
      for (let i = shuffled.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
      }
      playPlaylist(shuffled, 0);
    }
  };

  const sharePlaylist = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: playlist.name, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success('Link copied!');
      }
    } catch {}
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!playlist) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Playlist not found</p>
      </div>
    );
  }

  const isOwner = playlist.is_owner;

  return (
    <div className="min-h-screen py-8 px-4" data-testid="playlist-page">
      <div className="max-w-4xl mx-auto space-y-6">
        {/* Back Button */}
        <Button 
          variant="ghost" 
          onClick={() => navigate('/playlists')}
          className="mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Playlists
        </Button>

        {/* Playlist Header */}
        <Card className="p-6 bg-gradient-to-br from-primary/10 to-secondary/10 border border-border">
          {editMode ? (
            <div className="space-y-4">
              <div>
                <Label htmlFor="name">Playlist Name</Label>
                <Input
                  id="name"
                  value={editData.name}
                  onChange={(e) => setEditData(prev => ({ ...prev, name: e.target.value }))}
                  className="mt-1"
                  data-testid="edit-playlist-name"
                />
              </div>
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={editData.description}
                  onChange={(e) => setEditData(prev => ({ ...prev, description: e.target.value }))}
                  className="mt-1"
                  rows={3}
                  data-testid="edit-playlist-description"
                />
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  id="is_public"
                  checked={editData.is_public}
                  onCheckedChange={(checked) => setEditData(prev => ({ ...prev, is_public: checked }))}
                />
                <Label htmlFor="is_public" className="flex items-center gap-2">
                  {editData.is_public ? <Globe className="w-4 h-4" /> : <Lock className="w-4 h-4" />}
                  {editData.is_public ? 'Public' : 'Private'}
                </Label>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSaveChanges} disabled={saving} data-testid="save-playlist-btn">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Save className="w-4 h-4 mr-2" />}
                  Save Changes
                </Button>
                <Button variant="outline" onClick={() => setEditMode(false)}>
                  <X className="w-4 h-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col md:flex-row gap-6">
              {/* Playlist Cover */}
              <div className="w-40 h-40 flex-shrink-0 rounded-lg bg-gradient-to-br from-primary/30 to-secondary/30 flex items-center justify-center">
                {playlist.cover_url ? (
                  <img src={playlist.cover_url} alt={playlist.name} className="w-full h-full object-cover rounded-lg" />
                ) : (
                  <Music className="w-16 h-16 text-muted-foreground" />
                )}
              </div>

              {/* Playlist Info */}
              <div className="flex-1">
                <div className="flex items-start justify-between">
                  <div>
                    <Badge variant="outline" className="mb-2">
                      {playlist.is_public ? (
                        <><Globe className="w-3 h-3 mr-1" /> Public</>
                      ) : (
                        <><Lock className="w-3 h-3 mr-1" /> Private</>
                      )}
                    </Badge>
                    <h1 className="text-3xl font-bold" data-testid="playlist-title">{playlist.name}</h1>
                    {playlist.description && (
                      <p className="text-muted-foreground mt-2">{playlist.description}</p>
                    )}
                  </div>
                  
                  {/* Owner Controls */}
                  {isOwner && (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setEditMode(true)}
                      data-testid="edit-playlist-btn"
                    >
                      <Edit2 className="w-4 h-4 mr-2" />
                      Edit Playlist
                    </Button>
                  )}
                </div>

                <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <User className="w-4 h-4" />
                    {playlist.owner_name}
                  </span>
                  <span className="flex items-center gap-1">
                    <Music className="w-4 h-4" />
                    {playlist.track_count} tracks
                  </span>
                </div>

                <div className="flex gap-2 mt-4">
                  <Button onClick={playAllTracks} disabled={playlist.tracks?.length === 0} data-testid="play-all-btn">
                    <Play className="w-4 h-4 mr-2" fill="currentColor" />
                    Play All
                  </Button>
                  <Button variant="outline" onClick={shufflePlay} disabled={playlist.tracks?.length === 0} data-testid="shuffle-btn">
                    <Shuffle className="w-4 h-4 mr-2" />
                    Shuffle
                  </Button>
                  <Button variant="outline" onClick={sharePlaylist} data-testid="share-playlist-btn">
                    <Share2 className="w-4 h-4 mr-2" />
                    Share
                  </Button>
                  {isOwner && (
                    <Button variant="destructive" size="sm" onClick={handleDeletePlaylist} data-testid="delete-playlist-btn">
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        {/* Edit Mode Notice */}
        {editMode && isOwner && (
          <Card className="p-4 bg-amber-500/10 border-amber-500/30">
            <p className="text-sm text-amber-400 flex items-center gap-2">
              <Edit2 className="w-4 h-4" />
              Edit mode: Click the trash icon on any track to remove it from this playlist.
            </p>
          </Card>
        )}

        {/* Tracks List */}
        <Card className="p-6 bg-card/50 border border-border">
          <h2 className="text-xl font-semibold mb-4">Tracks</h2>
          
          {playlist.tracks?.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Music className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No tracks in this playlist yet</p>
              {isOwner && (
                <Button variant="outline" className="mt-4" onClick={() => navigate('/tracks')}>
                  <Plus className="w-4 h-4 mr-2" />
                  Browse Tracks
                </Button>
              )}
            </div>
          ) : (
            <div className="space-y-2" data-testid="playlist-tracks">
              {playlist.tracks.map((track, index) => (
                <div
                  key={track.track_id}
                  className="flex items-center gap-4 p-3 rounded-lg hover:bg-muted/30 transition-colors group"
                  data-testid={`playlist-track-${index}`}
                >
                  {/* Track Number / Drag Handle */}
                  <div className="w-8 text-center">
                    {editMode && isOwner ? (
                      <GripVertical className="w-4 h-4 text-muted-foreground mx-auto cursor-grab" />
                    ) : (
                      <span className="text-sm text-muted-foreground">{index + 1}</span>
                    )}
                  </div>

                  {/* Play Button / Cover */}
                  <div 
                    className="w-12 h-12 bg-muted rounded overflow-hidden relative flex-shrink-0 cursor-pointer"
                    onClick={() => playTrack(track, playlist.tracks)}
                  >
                    {track.cover_url ? (
                      <img src={track.cover_url} alt={track.title} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-full h-full bg-gradient-to-br from-primary/20 to-secondary/20 flex items-center justify-center">
                        <Music className="w-5 h-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="absolute inset-0 bg-background/50 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                      <Play className="w-5 h-5 text-white" fill="white" />
                    </div>
                  </div>

                  {/* Track Info */}
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{track.title}</p>
                    <p className="text-sm text-muted-foreground truncate">{track.artist_name}</p>
                  </div>

                  {/* Genre Badge */}
                  {track.primary_genre && (
                    <Badge variant="outline" className="hidden sm:flex text-xs">
                      {track.primary_genre}
                    </Badge>
                  )}

                  {/* Remove Button (Owner Only, Edit Mode) */}
                  {isOwner && editMode && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                      onClick={() => handleRemoveTrack(track.track_id)}
                      disabled={removingTrack === track.track_id}
                      data-testid={`remove-track-${track.track_id}`}
                    >
                      {removingTrack === track.track_id ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
